import { createSelector } from 'reselect';
import { isEmpty, groupBy } from 'lodash';
import { selectors as incidentResponseSelectors } from '../incident-responses';
import { selectors as activityReportAttendanceSelectors } from '../activity-report-attendances';
import { selectors as incidentSelectors } from '../incidents';
import { selectors as activityReportSelectors } from '../activity-reports';
import moment from 'moment';

const augmentedResponses = state => incidentResponseSelectors.augmentedAttendances(state);
const augmentedAttendances = state => activityReportAttendanceSelectors.augmentedAttendances(state);

// Returns users that have overlapping attendances
export const overlappingAttendancesByUserId = createSelector(
  augmentedResponses,
  augmentedAttendances,
  incidentSelectors.byId,
  activityReportSelectors.byId,
  (responses, attendances, incidentsById, activitiesById) => {
    var result = {};

    const allAttendances = [
      ...responses
        .filter(r => r.reported_status === 'dispatched')
        .map(r => ({
          ...r,
          activityId: r.incident_id,
          activity: incidentsById[r.incident_id] || {
            id: r.incident_id,
            type: null
          }
        })),
      ...attendances
        .filter(a => a.reported_status === 'attended')
        .map(a => ({
          ...a,
          activityId: a.activity_report_id,
          activity: activitiesById[a.activity_report_id] || {
            id: a.activity_report_id,
            type: null
          }
        }))
    ];

    const byUserId = groupBy(allAttendances, i => i.user_id);

    for (const [userId, userItems] of Object.entries(byUserId)) {
      // If there's only one attendance or less, there can't be any overlaps
      if (userItems.length <= 1) continue;

      const overlappingAttendances = userItems.filter(attendance => {
        const startTime = moment(attendance.start_time);
        const endTime = moment(attendance.end_time);

        return userItems.some(
          other => attendance.id !== other.id && startTime.isBefore(other.end_time) && endTime.isAfter(other.start_time)
        );
      });

      if (!isEmpty(overlappingAttendances)) result[userId] = overlappingAttendances;
    }

    return result;
  }
);

export const activityOverlappingAttendances = createSelector(
  overlappingAttendancesByUserId,
  attendancesByUserId =>
    (activityId, userIds = []) =>
      Object.values(attendancesByUserId)
        .flat()
        .filter(a => a.activityId !== activityId && (isEmpty(userIds) || userIds.includes(a.user_id)))
);

export default {
  overlappingAttendancesByUserId,
  activityOverlappingAttendances
};
